import * as React from "react";
import { useEffect, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import { FetchAPIData } from "../../utils/GlobalFunctions/fetch.ts";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store.ts";
import { sessionDataType } from "../../utils/types/app.types.ts";
import SliderModule from "../../components/SliderModule/index.tsx";
import MediaCastSection from "../../components/MediaCastSection/index.tsx";
import { normalizeMovieCast } from "../../components/MediaCastSection/castUtils.ts";
import MediaReviewsSection from "../../components/MediaReviewsSection/index.tsx";

export type ProductionCompaniesType = {
  id: number;
  name: string;
  logo_path: string;
  origin_country: string;
};

export type MovieDetailsType = {
  production_companies?: ProductionCompaniesType[];
  status?: boolean;
  backdrop_path?: string;
  poster_path?: string;
  title?: string;
  vote_average?: number;
  release_date?: string;
  overview?: string;
  homepage?: string;
  budget?: string;
  revenue?: string;
  runtime?: string;
  genres?: [];
  credits?: {
    cast?: Array<{
      id: number;
      name: string;
      character?: string;
      order?: number | null;
      profile_path?: string | null;
    }>;
  };
};

const intiliaze = async (movieId: string, sessionData: sessionDataType) => {
  const newMovieDetails: MovieDetailsType = await FetchAPIData(
    `movie/${movieId}`,
    sessionData,
    { append_to_response: "credits" },
  );
  //console.log(newMovieDetails);

  //TODO Create response validation check to & show error message if it fails

  return newMovieDetails;
};

export const MovieDetailsPage: React.FC<{}> = (props) => {
  const sessionData = useSelector((state: RootState) => state.session);
  const { movieId = "" } = useParams<{ movieId: string }>();
  const location = useLocation();
  const [movieDetails, setMovieDetails] = useState<MovieDetailsType>();

  // Get URL parameters
  const searchParams = new URLSearchParams(location.search);
  const fromGenre = searchParams.get('from') === 'genre';
  const genreName = searchParams.get('genreName');
  const genreId = searchParams.get('genreId');
  const mediaType = searchParams.get('mediaType');

  useEffect(() => {
    //console.log(sessionData.api.apiKey);

    intiliaze(movieId, sessionData).then((data) => {
      setMovieDetails(data);
    });
  }, [movieId, sessionData]);

  // Scroll to top when movieId changes (when clicking recommended movies)
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [movieId]);

  // Determine back button text and link
  const getBackButton = () => {
    if (fromGenre && genreName && genreId && mediaType) {
      return (
        <Link className="btn" to={`/genres/${mediaType}/${genreId}`}>
          ← Back to {genreName}
        </Link>
      );
    }
    
    return (
      <Link className="btn" to="/movies">
        ← Back to Movies
      </Link>
    );
  };

  return (
    <>
      {/* Movie Details — media-details-page keeps light text over backdrop in all themes */}
      <section className="container media-details-page">
        <div id="movie-details" className="media-details">
          {movieDetails && movieDetails.backdrop_path && (
            <div
              className="movie-details-backdrop"
              style={{
                backgroundImage: `linear-gradient(
                  180deg,
                  rgba(2, 13, 24, 0.62) 0%,
                  rgba(2, 13, 24, 0.5) 45%,
                  rgba(2, 13, 24, 0.68) 100%
                ), url("https://image.tmdb.org/t/p/original/${movieDetails.backdrop_path}")`,
              }}
            ></div>
          )}

          <div className="back">{getBackButton()}</div>

          {/* Movie Details Output */}
          {typeof movieDetails === "object" && (
            <div>
              <div className="details-top">
                <div>
                  <img
                    src={
                      typeof movieDetails.poster_path === "string" &&
                      movieDetails.poster_path.length
                        ? `https://image.tmdb.org/t/p/w500${movieDetails.poster_path}`
                        : "../images/no-image.jpg"
                    }
                    className="card-img-top"
                    alt={movieDetails.title}
                  />
                </div>
                <div style={{ marginLeft: "30px" }}>
                  <h2>{movieDetails.title}</h2>
                  <p>
                    <FontAwesomeIcon
                      icon={faStar}
                      style={{ color: "#f1c40f" }}
                    />
                    &nbsp;
                    {movieDetails["vote_average"] !== undefined &&
                      movieDetails.vote_average.toFixed(1)}{" "}
                    / 10
                  </p>
                  <p className="text-muted">
                    Release Date:
                    {movieDetails.release_date && movieDetails.release_date}
                  </p>
                  <p>{movieDetails.overview && movieDetails.overview}</p>
                  <h5>Genres</h5>
                  <ul className="list-group">
                    {Array.isArray(movieDetails["genres"]) &&
                      movieDetails.genres.map(
                        (genre: { id: number; name: string }) => {
                          return <li key={genre.id}>{genre.name}</li>;
                        },
                      )}
                  </ul>
                  <a
                    href={
                      movieDetails["homepage"] !== undefined
                        ? movieDetails.homepage
                        : ""
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="btn"
                  >
                    Visit Movie Homepage
                  </a>
                </div>
              </div>
              <div className="details-bottom">
                <h2>Movie Info</h2>
                <ul>
                  <li>
                    <span className="text-secondary">Budget:</span>$
                    {movieDetails.budget &&
                      movieDetails.budget
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  </li>
                  <li>
                    <span className="text-secondary">Revenue:</span> $
                    {movieDetails.revenue &&
                      movieDetails.revenue
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  </li>
                  <li>
                    <span className="text-secondary">Runtime:</span>{" "}
                    {movieDetails.runtime && movieDetails.runtime} minutes
                  </li>
                  <li>
                    <span className="text-secondary">Status:</span>{" "}
                    {movieDetails.status && movieDetails.status}
                  </li>
                </ul>
                <h4>Production Companies</h4>
                <div className="list-group">
                  {Array.isArray(movieDetails["production_companies"]) &&
                    movieDetails.production_companies.map((company, index) => {
                      return (
                        <span key={company.id}>
                          {(index ? ", " : "") + company.name}
                        </span>
                      );
                    })}
                </div>
              </div>
              <MediaReviewsSection mediaType="movie" mediaId={movieId} />
              <MediaCastSection
                cast={normalizeMovieCast(movieDetails.credits?.cast)}
              />
            </div>
          )}
        </div>
      </section>
      <SliderModule id={movieId} mediaType="movie" />
    </>
  );
};
export default MovieDetailsPage;